import type {AnyPcbBakedNode, BakedRulesFor, PcbBakedNode, PcbNodeTypes, PcbNodesMap} from "@buildwithflux/core";
import {useMemo} from "react";
import {useSyncExternalStoreWithSelector} from "use-sync-external-store/with-selector";

import type {FluxReactivitySubscriptions} from "modules/reactivity/subscriptions";

import {EqualityFn} from "./models";

export type PcbNodeSelector<Selection extends unknown> = (node: AnyPcbBakedNode | undefined) => Selection | undefined;
const defaultSelector: PcbNodeSelector<AnyPcbBakedNode> = (node) => node;

type UsePcbNodeWithoutSelector = <T extends unknown>(
    nodeUid: string | undefined | null,
) => undefined | (T extends PcbNodeTypes ? PcbBakedNode<T> : AnyPcbBakedNode);
type UsePcbNodeWithSelector = <Selection extends unknown>(
    nodeUid: string | undefined | null,
    selector: PcbNodeSelector<Selection>,
    isEqual?: EqualityFn,
) => Selection;
/**
 * Use this to reactively subscribe to one single pcb node
 */
export type UsePcbNode = UsePcbNodeWithoutSelector & UsePcbNodeWithSelector;

export const createUsePcbNode = (reactivitySubscriptions: FluxReactivitySubscriptions): UsePcbNode => {
    function usePcbNode<_Selection extends unknown>(nodeUid: string | undefined | null): undefined | AnyPcbBakedNode;
    function usePcbNode<Selection extends unknown>(
        nodeUid: string | undefined | null,
        selector: PcbNodeSelector<Selection>,
        isEqual?: EqualityFn,
    ): undefined | Selection;
    function usePcbNode<Selection extends unknown>(
        nodeUid: string | undefined | null,
        selector?: PcbNodeSelector<Selection>,
        isEqual?: EqualityFn,
    ): undefined | Selection | AnyPcbBakedNode {
        // It is important to memoize the subscribe function here; otherwise, React will re-subscribe
        // between re-renders
        // See notes: https://react.dev/reference/react/useSyncExternalStore#my-subscribe-function-gets-called-after-every-re-render
        const subscribe = useMemo(() => {
            return reactivitySubscriptions.makeSubscriptionToPcbNode(nodeUid);
        }, [nodeUid]);
        const getSnapshot = useMemo(() => {
            return reactivitySubscriptions.makeGetPcbNodeSnapshot(nodeUid);
        }, [nodeUid]);

        return useSyncExternalStoreWithSelector<AnyPcbBakedNode | undefined, AnyPcbBakedNode | Selection | undefined>(
            subscribe,
            getSnapshot,
            undefined,
            selector ? selector : defaultSelector,
            isEqual,
        );
    }

    return usePcbNode;
};

export type UsePcbNodeBakedRules = ((
    nodeUid: string | undefined | null,
    isEqual?: EqualityFn,
) => undefined | BakedRulesFor<PcbNodeTypes>) &
    (<T extends PcbNodeTypes>(
        nodeUid: string | undefined | null,
        type: T,
        isEqual?: EqualityFn,
    ) => undefined | BakedRulesFor<T>);
export const createUsePcbNodeBakedRules = (
    reactivitySubscriptions: FluxReactivitySubscriptions,
): UsePcbNodeBakedRules => {
    function usePcbNodeBakedRules<_T extends PcbNodeTypes>(
        nodeUid: string | undefined | null,
        isEqual?: EqualityFn,
    ): undefined | BakedRulesFor<PcbNodeTypes>;
    function usePcbNodeBakedRules<T extends PcbNodeTypes>(
        nodeUid: string | undefined | null,
        type: T,
        isEqual?: EqualityFn,
    ): undefined | BakedRulesFor<T>;
    function usePcbNodeBakedRules<T extends PcbNodeTypes>(
        nodeUid: string | undefined | null,
        isEqualOrType?: EqualityFn | T,
        isEqual?: EqualityFn,
    ): undefined | BakedRulesFor<PcbNodeTypes> | BakedRulesFor<T> {
        const equalityFn = isEqual
            ? isEqual
            : isEqualOrType && typeof isEqualOrType === "function"
            ? isEqualOrType
            : undefined;

        // It is important to memoize the subscribe function here; otherwise, React will re-subscribe
        // between re-renders
        // See notes: https://react.dev/reference/react/useSyncExternalStore#my-subscribe-function-gets-called-after-every-re-render
        const subscribe = useMemo(() => {
            return reactivitySubscriptions.makeSubscriptionToPcbNodeBakedRules(nodeUid);
        }, [nodeUid]);
        const getSnapshot = useMemo(() => {
            return reactivitySubscriptions.makeGetPcbNodeBakedRulesSnapshot(nodeUid);
        }, [nodeUid]);
        return useSyncExternalStoreWithSelector<
            BakedRulesFor<PcbNodeTypes> | undefined,
            BakedRulesFor<PcbNodeTypes> | undefined
        >(subscribe, getSnapshot, undefined, (bakedRules) => bakedRules, equalityFn);
    }

    return usePcbNodeBakedRules;
};

type UseAllPcbNodesWithoutSelector = () => PcbNodesMap<AnyPcbBakedNode>;
type UseAllPcbNodesWithSelector = <Selection extends unknown>(
    selector: (nodes: PcbNodesMap<AnyPcbBakedNode>) => Selection,
    isEqual?: EqualityFn,
) => Selection;
/**
 * @deprecated This hook subscribes to the entire PCB nodes collection which can contain millions of objects.
 * Any change to any PCB node will trigger re-renders in components using this hook.
 * Use targeted hooks instead:
 * - useComponentsContainerNode - for finding the Components container node
 * - useSelectedPcbLayoutNodes - for getting selected PCB layout nodes
 * - useAllPcbNodesNonReactive - for non-reactive access to all PCB nodes
 */
export type UseAllPcbNodes = UseAllPcbNodesWithoutSelector & UseAllPcbNodesWithSelector;

export const createUseAllPcbNodes = (reactivitySubscriptions: FluxReactivitySubscriptions): UseAllPcbNodes => {
    function useAllPcbNodes(): PcbNodesMap<AnyPcbBakedNode>;
    function useAllPcbNodes<Selection extends unknown>(
        selector: (nodes: PcbNodesMap<AnyPcbBakedNode>) => Selection,
        isEqual?: EqualityFn,
    ): Selection;
    function useAllPcbNodes<Selection extends unknown>(
        selector?: (nodes: PcbNodesMap<AnyPcbBakedNode>) => Selection,
        isEqual?: EqualityFn,
    ): Selection | PcbNodesMap<AnyPcbBakedNode> {
        const subscribe = useMemo(() => {
            return reactivitySubscriptions.subscribeToAllPcbNodes;
        }, []);
        const getSnapshot = useMemo(() => {
            return reactivitySubscriptions.getPcbNodesSnapshot;
        }, []);

        return useSyncExternalStoreWithSelector<PcbNodesMap<AnyPcbBakedNode>, Selection | PcbNodesMap<AnyPcbBakedNode>>(
            subscribe,
            getSnapshot,
            undefined,
            selector ? selector : (nodes) => nodes,
            isEqual,
        );
    }

    return useAllPcbNodes;
};
